import { useQuery } from '@tanstack/react-query';
import { listTasks, type Task, type TaskFilter } from '../api';

/**
 * The Board's sentence about finishing, and the one place it is computed.
 *
 * It lived inline in `TaskBoard.tsx`, the only screen that "understood my situation" to the ADHD
 * persona — two clicks from Today, where the eleven started rows actually were. Today now says it
 * too (`01a02f8e-b626`, absorbing `01a03cd8-abb3`), and it says it *from the Board's own query*
 * rather than counting its own shortlist: the shortlist drops a started task that is waiting or
 * blocked, so a count over it would put a second, smaller "things in progress" on the first
 * screen while the Board printed the real one. One sentence, one query, one rule for which rows
 * the Board shows — all three are in this file, and the Board reads them from here.
 */

/**
 * How many things in progress before the board says something.
 *
 * Warn, never block — §4. Three is the number where "what am I working on" stops having an
 * answer; on both of the owner's real accounts the count is zero, so this will not fire at
 * anyone until they have actually started using the state.
 */
const WIP_SOFT_LIMIT = 3;

/**
 * The Board's query: its three columns in one round trip.
 *
 * One query for the three columns rather than three: the API takes an array of statuses since
 * Phase 37, and one round trip keeps the columns consistent with each other.
 */
export function boardFilter(filter: TaskFilter): TaskFilter {
  return { ...filter, status: ['inbox', 'next', 'in_progress'] };
}

/**
 * The rows the Board draws, of the rows its query returned.
 *
 * The same rule the list applies, so the two views cannot report different amounts of work.
 *
 * A child whose parent is *not* on the board stays: it renders as a card of its own and its
 * parent link already says where it came from — the same escape hatch the list's grouping has,
 * and the reason a filtered board never silently loses a task.
 *
 * "On the board" has to mean *in this column*, which it did not. A subtask was suppressed whenever
 * its parent existed anywhere, so three `inbox` children of a parent sitting in **In progress**
 * were drawn nowhere at all — while the Inbox heading, which counts what the column holds, still
 * counted them. Inbox claimed 12 and drew 9, and the parent showed a bare "0/3" chip with nothing
 * to expand. Two correct changes met: Phase 27 taught the heading to count tasks rather than
 * cards, Phase 34 grouped children under their parent, and between them a column counted three
 * rows it could not reach.
 *
 * The board's own column layout already says this is the rule — "a child whose parent is in a
 * *different* column keeps its own place and its chip" — and could never take effect, because the
 * child had been filtered out before it.
 */
export function onTheBoard(all: readonly Task[], showSubtasks: boolean): Task[] {
  if (showSubtasks) return [...all];
  const byId = new Map(all.map((t) => [t.id, t]));
  return all.filter((t) => {
    if (!t.parentTaskId) return true;
    const parent = byId.get(t.parentTaskId);
    return !(parent && parent.status === t.status);
  });
}

/** The sentence, when the count earns it. */
export function WipNotice({ count }: { count: number }) {
  if (count <= WIP_SOFT_LIMIT) return null;
  return (
    <p className="notice warn" role="status" data-testid="wip-notice">
      {count} things in progress. Finishing one beats starting another.
    </p>
  );
}

/**
 * The sentence on Today, as the Board would print it with no filter set.
 *
 * `scope: 'mine'` is the Board's own default — what `TaskList` hands it before anybody picks a
 * project — and subtasks folded is its default too, so this is the number the Board tab shows the
 * moment it is opened. The query key is the Board's, so the two share a cache entry when they can.
 */
export function TodayWip() {
  return <WipNotice count={useInProgressCount() ?? 0} />;
}

/**
 * The Board's in-progress figure, from the Board's own query — or `null` until it has arrived.
 *
 * One hook for the sentence above and for the readback a Start press leaves (`01a15a31-174b`):
 * *Started "…" — N in progress* must print the number the Board column and the WIP sentence print,
 * or the screen holds two counts of one pile — the defect `01a15a31-14a5` closed for the late one.
 * `null` rather than zero while loading, so a caller can leave the clause out instead of claiming
 * nothing is in progress.
 */
export function useInProgressCount(): number | null {
  return useInProgressRows()?.length ?? null;
}

/**
 * The rows that figure counts: the Board's *In progress* column as it opens, subtasks folded.
 *
 * Today's *Started* heading reads these to name the in-progress rows its shortlist does not hold
 * (`inProgressElsewhere`), so the heading and the sentence above it add up rather than print two
 * figures over one word.
 */
export function useInProgressRows(): Task[] | null {
  const filter = boardFilter({ scope: 'mine' });
  const tasks = useQuery({ queryKey: ['tasks', filter], queryFn: () => listTasks(filter) });
  if (!tasks.data) return null;
  return onTheBoard(tasks.data, false).filter((t) => t.status === 'in_progress');
}
